import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LnPrimaryButtonComponent } from '@components/butons/ln-primary-button/ln-primary-button.component';
import intlTelInput, { Iti, SomeOptions } from 'intl-tel-input';
import { MenuActions } from '../../states/menu_actions';
import { NgClass } from '@angular/common';
import { ContactPageFormDropDownComponent } from '@components/contact-page-form-drop-down/contact-page-form-drop-down.component';
import { FullScreenModalComponent } from '@components/modals/full-screen-modal/full-screen-modal.component';
import { SuccessfulOrderCardComponent } from '@components/cards/successful-order-card/successful-order-card.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@interfaces/api/api.interface';
import { ContactService } from '@core/services/contact/contact.service';
import { CheckBoxAndLabelInterface } from '@interfaces/contact/check-box-and-label-interface';
import { ContactOptionsInterface } from '@interfaces/contact/contact-options-interface';
import { ContactInterface } from '@interfaces/contact/contact-interface';
import { TranslocoPipe } from '@jsverse/transloco';
import { LanguageService } from '@core/services/language/language.service';
import { CaptchaComponent } from '@components/captcha/captcha.component';

@Component({
  selector: 'ln-contact-page-form-of-contact',
  imports: [
    FormsModule,
    LnPrimaryButtonComponent,
    ReactiveFormsModule,
    NgClass,
    ContactPageFormDropDownComponent,
    FullScreenModalComponent,
    SuccessfulOrderCardComponent,
    TranslocoPipe,
    CaptchaComponent,
  ],
  templateUrl: './contact-page-form-of-contact.component.html',
  styleUrl: './contact-page-form-of-contact.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactPageFormOfContactComponent implements OnInit, AfterViewInit {
  private TAG = 'contact-page-form-of-contact';
  @ViewChild('phoneInput', { static: false }) phoneInput!: ElementRef;
  contadorCaracteresMessage = 0;
  form: FormGroup;
  iti?: Iti;
  protected enviarButtonShowLoading: WritableSignal<boolean> = signal(false);
  showSuccessfulModal: WritableSignal<boolean> = signal(false);
  @Output() showUnSuccessfulModal = new EventEmitter<void>();
  showContactOptionError: WritableSignal<boolean> = signal(false);
  private destroyRef = inject(DestroyRef);
  private menuActions = inject(MenuActions);
  private contactService = inject(ContactService);
  protected listOfOptions?: WritableSignal<CheckBoxAndLabelInterface>[];
  private locale = inject(LanguageService);
  protected captchaToken: WritableSignal<string | undefined> = signal(undefined);
  constructor() {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.pattern(/^[\p{L}\p{N}\s'’-]+$/u)]),
      phonePrefix: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required, Validators.pattern('^[0-9]+$')]),
      email: new FormControl('', [Validators.required, Validators.email]),
      message: new FormControl('', [Validators.maxLength(250)]),
      contactOption: new FormControl(''),
      sendCopy: new FormControl(true),
      cfToken: new FormControl(''),
    });

    effect(() => {
      this.locale.currentLanguage();
      this.getContactOptions();
    });
  }
  ngOnInit(): void {
    this.addPMessageInputListener();
  }

  getContactOptions() {
    this.contactService
      .getContactOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<ContactOptionsInterface[]>) => {
          if (res.success) {
            console.log(`${this.TAG} ${res.data.length}`);
            this.updateListOfOptions(res.data);
          } else {
            console.log(`${this.TAG} orderService error`);
          }
        },
        error: err => {
          console.log(`${this.TAG} orderService error: ${err}`);
        },
      });
  }

  updateListOfOptions(contactOptions: ContactOptionsInterface[]) {
    const listOfOptions: WritableSignal<CheckBoxAndLabelInterface>[] = [];
    contactOptions.forEach(option => {
      const checkBoxAndLabelInterface: CheckBoxAndLabelInterface = {
        id: option.id,
        label: option.descripcion,
        state: false,
      };
      listOfOptions.push(signal(checkBoxAndLabelInterface));
    });
    if (listOfOptions.length > 0) {
      this.listOfOptions = listOfOptions;
    }
  }

  ngAfterViewInit() {
    if (this.phoneInput) {
      const options: SomeOptions = { initialCountry: 'gt' };
      this.iti = intlTelInput(this.phoneInput.nativeElement, options);
      //set initial Guatemala dial code
      this.setDialCode('502');
      this.addPhoneInputListener();
    } else {
      console.warn('phoneInput no encontrado en ngAfterViewInit');
    }
  }

  addPMessageInputListener() {
    this.form.controls['message'].valueChanges.subscribe(value => {
      this.contadorCaracteresMessage = value.length;
    });
  }
  addPhoneInputListener() {
    this.phoneInput.nativeElement.addEventListener('countrychange', () => {
      const dialCode: string | undefined = this.iti?.getSelectedCountryData().dialCode;
      console.log('Código de país:', dialCode);
      if (dialCode) {
        this.setDialCode(dialCode);
      }
    });
  }
  setDialCode(dialCode: string) {
    this.form.get('phonePrefix')?.setValue(dialCode, { emitEvent: false });
  }

  preventTyping(event: KeyboardEvent) {
    event.preventDefault(); // Stops any character input
  }

  closeSuccessfulModal(): void {
    this.showSuccessfulModal.set(false);
    this.menuActions.goHome();
  }

  selectedContactOptions(): string[] {
    const contactOptions: string[] = [];
    if (this.listOfOptions) {
      this.listOfOptions.forEach(option => {
        if (option().state) {
          contactOptions.push(option().id);
        }
      });
    }
    return contactOptions;
  }

  onSubmit(): void {
    const contactOptions = this.selectedContactOptions();
    console.log(`${this.TAG} onSubmit() contactOptions.length == ${contactOptions.length}`);
    if (this.form.valid && contactOptions.length > 0 && this.captchaToken() && this.captchaToken()?.length) {
      const dialCode = this.iti?.getSelectedCountryData().dialCode;
      const phoneNumber = this.form.value.phoneNumber;
      const fullPhone = `+${dialCode}${phoneNumber}`;
      const mesagge: string = this.form.value.message;
      const contact: ContactInterface = {
        nombreCompleto: this.form.value.name,
        telefono: fullPhone,
        email: this.form.value.email,
        mensaje: mesagge.length ? mesagge : undefined,
        contactOptionIDs: contactOptions,
        sendMeACopy: this.form.value.sendCopy,
      };
      this.enviarButtonShowLoading.set(true);
      this.contactService
        .sendContact(contact, this.captchaToken())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: ApiResponse<void>) => {
            if (res.success) {
              this.showSuccessfulModal.set(true);
              this.enviarButtonShowLoading.set(false);
            } else {
              console.log(`${this.TAG} orderService error`);
              this.showUnSuccessfulModal.emit();
            }
          },
          error: err => {
            this.enviarButtonShowLoading.set(false);
            this.showUnSuccessfulModal.emit();
            console.log(`${this.TAG} orderService error: ${err}`);
          },
        });
    } else {
      // console.log(`${this.TAG} onSubmit() form invalid`);
      if (contactOptions.length == 0) {
        this.showContactOptionError.set(true);
      }
      this.form.markAllAsTouched();
    }
  }
}
